import { memo, useEffect, useRef, useState } from 'react';
import { playMilestone } from '../../utils/xpSound';

/**
 * Barre de progression de la passation (extraite de Quiz.jsx) — affichage seul.
 * Barre segmentée orange (une par partie) + jalon étoile à mi-parcours avec
 * toast « Mi-parcours ! » one-shot. (Le marcheur Tano a été retiré le
 * 2026-07-29 : il flottait 30 px au-dessus de la barre dans l'en-tête
 * une-ligne — la barre seule, centrée, est le repère.) Aucune logique de
 * réponse ici.
 */
const QuizProgress = memo(function QuizProgress({
    partsOrder, partCounts, currentPartIndex, currentQuestionIndex,
    totalQuestions, globalCurrent, globalTotal, hasMultipleParts,
}) {
    const miParcoursAtteint = globalTotal >= 4 && globalCurrent > globalTotal / 2;

    // Toast mi-parcours : une seule fois par quiz.
    const toastMontreRef = useRef(false);
    const [showToast, setShowToast] = useState(false);
    useEffect(() => {
        if (!miParcoursAtteint || toastMontreRef.current) return undefined;
        toastMontreRef.current = true;
        setShowToast(true);
        playMilestone();
        const t = setTimeout(() => setShowToast(false), 1900);
        return () => clearTimeout(t);
    }, [miParcoursAtteint]);

    return (
        <div className="flex items-center gap-2">
            {/* Zone barre : hauteur réservée au-dessus pour le Tano avanceur */}
            <div className="relative flex-1">
                {/* Toast mi-parcours (one-shot) */}
                {showToast && (
                    <div
                        className="quiz-milestone-toast absolute left-1/2 -translate-x-1/2 -top-2 z-10 px-3 py-1 rounded-full text-xs font-extrabold whitespace-nowrap"
                        style={{ fontFamily: 'Fredoka', background: 'rgba(255,185,56,.95)', color: '#3b2303', boxShadow: '0 6px 16px rgba(0,0,0,.3)' }}
                    >
                        Mi-parcours ! 💪
                    </div>
                )}

                {/* Jalon étoile à 50 % (s'allume une fois franchi) */}
                {globalTotal >= 4 && (
                    <span
                        className="absolute text-[11px] pointer-events-none"
                        style={{
                            left: '50%', bottom: -3, transform: 'translateX(-50%)',
                            filter: miParcoursAtteint ? 'none' : 'grayscale(1)',
                            opacity: miParcoursAtteint ? 1 : 0.45,
                            transition: 'opacity .3s ease, filter .3s ease',
                        }}
                    >
                        ⭐
                    </span>
                )}

                {/* Barre segmentée (une par partie) ou barre simple.
                    Largeur de chaque segment PROPORTIONNELLE à son nombre de
                    questions (flexGrow = partCounts[i]) — sinon une partie de
                    12 questions et une de 1 feraient un 50/50 visuel,
                    désynchronisé de la progression réelle. */}
                {hasMultipleParts ? (
                    <div className="flex items-center gap-1">
                        {partsOrder.map((p, i) => {
                            const fill = i < currentPartIndex
                                ? 1
                                : i === currentPartIndex
                                    ? (currentQuestionIndex + 1) / Math.max(1, partCounts[i])
                                    : 0;
                            return (
                                <div
                                    key={p}
                                    className="h-3.5 rounded-full overflow-hidden"
                                    style={{
                                        flexGrow: Math.max(1, partCounts[i]),
                                        flexBasis: 0,
                                        minWidth: 10, // une partie d'1 question reste visible
                                        background: 'rgba(255,255,255,.12)',
                                    }}
                                >
                                    <div
                                        className="h-full rounded-full transition-all duration-300"
                                        style={{ width: `${Math.round(fill * 100)}%`, background: 'linear-gradient(90deg,#F59E0B,#F97316)' }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="h-3.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,.12)' }}>
                        <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{ width: `${Math.round(((currentQuestionIndex + 1) / Math.max(1, totalQuestions)) * 100)}%`, background: 'linear-gradient(90deg,#F59E0B,#F97316)' }}
                        />
                    </div>
                )}
            </div>
            <span className="text-xs font-extrabold tabular-nums flex-shrink-0" style={{ color: 'rgba(255,255,255,.8)' }}>
                {globalCurrent}/{globalTotal}
            </span>
        </div>
    );
});

export default QuizProgress;
